import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Music2, Sparkles, Instagram } from "lucide-react";

const DJ_IMG =
  "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/kdd7sjwz_IMG_2364.JPG";

const GENRES = [
  "Punjabi",
  "Bollywood",
  "Bhangra",
  "English",
  "Hip-Hop",
  "R&B",
  "Top 40",
];

const HIGHLIGHTS = [
  { stat: "7+", label: "Years of Experience" },
  { stat: "Founder", label: "Karnival Events" },
  { stat: "5.0★", label: "Google Rating" },
  { stat: "All-In-One", label: "DJ Experience" },
];

export default function DjProfile() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#070707] min-h-screen text-[#FDFBF7]" data-testid="dj-profile-page">
      {/* Hero portrait */}
      <section className="relative pt-28 md:pt-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <button
            onClick={() => navigate("/")}
            data-testid="dj-profile-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>

          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-stretch">
            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              className="md:col-span-6 relative"
            >
              <div className="relative overflow-hidden rounded-sm aspect-[4/5] sm:aspect-[3/4] md:aspect-auto md:h-full">
                <img
                  src={DJ_IMG}
                  alt="DJ Avtaar performing — Karnival Events founder"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="dj-profile-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/70">
                      Founder
                    </div>
                    <div className="font-serif text-3xl md:text-4xl text-white mt-1 italic">
                      DJ Avtaar
                    </div>
                  </div>
                  <Sparkles
                    className="h-6 w-6 text-white"
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="md:col-span-6"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-white" />
                <span className="text-xs uppercase tracking-[0.28em] text-white">
                  Meet the Artist
                </span>
              </div>

              <h1
                data-testid="dj-profile-headline"
                className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-white tracking-tight"
              >
                The man behind the{" "}
                <span className="italic text-white/85">decks</span>.
              </h1>

              <div className="mt-8 space-y-5 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
                <p data-testid="dj-profile-bio-1">
                  <span className="text-white">DJ Avtaar</span> — founder of the
                  company and a seasoned DJ with over 7 years of experience —
                  leads our entertainment services with high-energy
                  performances across weddings, corporate events, and private
                  celebrations. Known for his seamless mixing and ability to
                  read the crowd, he&rsquo;s built a reputation for creating
                  unforgettable dance floors.
                </p>
                <p data-testid="dj-profile-bio-2">
                  Backed by a talented team of professional DJs, DJ Avtaar
                  ensures every event delivers the same energy, passion, and
                  precision. Our diverse music library includes Punjabi,
                  Bollywood, Bhangra, English, Hip-Hop, R&amp;B, and Top 40 hits
                  — customized to match your vibe. Paired with premium sound,
                  lighting, and optional dholi performances, we deliver an
                  all-in-one DJ experience that keeps your guests dancing and
                  the memories flowing.
                </p>
              </div>

              {/* Genres */}
              <div className="mt-10">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
                  Music we spin
                </div>
                <div className="flex flex-wrap gap-2" data-testid="dj-profile-genres">
                  {GENRES.map((g) => (
                    <span
                      key={g}
                      className="text-xs px-3.5 py-2 rounded-full border border-white/15 text-white/70 bg-white/[0.02]"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="dj-profile-cta-book"
                  className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-white/90 transition-colors"
                >
                  Book DJ Avtaar
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/gallery?filter=av"
                  data-testid="dj-profile-cta-gallery"
                  className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/15 text-white hover:border-white transition-colors"
                >
                  <Music2 className="h-4 w-4" />
                  See AV Setups
                </Link>
                <a
                  href="https://www.instagram.com/avtaarmusic_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dj-profile-instagram"
                  className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/15 text-white/80 hover:text-black hover:bg-white hover:border-white transition-all duration-300"
                >
                  <Instagram className="h-4 w-4" strokeWidth={1.5} />
                  @avtaarmusic_
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights strip */}
      <section className="px-6 md:px-12 mt-24 md:mt-32 mb-32">
        <div className="max-w-[1400px] mx-auto border-y border-white/10 py-10">
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12"
            data-testid="dj-profile-highlights"
          >
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.label}
                className="border-l border-white/10 pl-4 md:pl-6"
              >
                <div className="font-serif text-3xl md:text-4xl text-white">
                  {h.stat}
                </div>
                <div className="text-xs md:text-sm text-white/50 uppercase tracking-wider mt-1">
                  {h.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
